/**
 * Obiective per Canal × Categorie produs × Lună.
 * Canalele includ magazinele (Iași/Bacău/Cluj/Oradea) alături de Telesales/D2D.
 * Editare manuală pe fiecare lună; Q1-Q4 + Total se calculează automat.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Target, Save, RefreshCw, Loader2, Award } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import ProductPointsPanel from "../components/ProductPointsPanel";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const MONTHS = ["Ian", "Feb", "Mar", "Apr", "Mai", "Jun", "Iul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const QUARTERS = [
  { key: "Q1", months: [1, 2, 3] },
  { key: "Q2", months: [4, 5, 6] },
  { key: "Q3", months: [7, 8, 9] },
  { key: "Q4", months: [10, 11, 12] },
];
const YEARS = [2024, 2025, 2026, 2027, 2028];

export default function ObjectivesMatrixPage() {
  const { hasRole } = useAuth();
  const canWrite = hasRole(["admin", "manager", "backoffice", "bo"]);

  const [meta, setMeta] = useState({ channels: [], categories: [] });
  const [year, setYear] = useState(2026);
  const [channel, setChannel] = useState("telesales");
  const [matrix, setMatrix] = useState({});
  const [progress, setProgress] = useState({});
  const [hvasUnmapped, setHvasUnmapped] = useState(0);
  const [mode, setMode] = useState("objective"); // objective | realized | percent
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/channel-objectives/meta`)
      .then((r) => {
        setMeta(r.data);
        if (r.data.channels?.[0]) setChannel((c) => c || r.data.channels[0].key);
      })
      .catch(() => toast.error("Nu am putut încărca metadatele"));
  }, []);

  const fetchMatrix = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/channel-objectives`, { params: { year, channel } }),
        axios.get(`${API_URL}/channel-objectives/progress`, { params: { year, channel } }),
      ]);
      setMatrix(mRes.data.matrix || {});
      setProgress(pRes.data.matrix || {});
      setHvasUnmapped(pRes.data.hvas_unmapped_contracts || 0);
      setDirty(false);
    } catch (e) {
      toast.error("Nu am putut încărca obiectivele");
    } finally {
      setLoading(false);
    }
  }, [year, channel]);

  useEffect(() => { fetchMatrix(); }, [fetchMatrix]);

  const setCell = (cat, month, raw) => {
    const val = raw === "" ? 0 : Math.max(parseInt(raw, 10) || 0, 0);
    setMatrix((prev) => ({ ...prev, [cat]: { ...(prev[cat] || {}), [month]: val } }));
    setDirty(true);
  };

  const cellVal = (cat, month) => (matrix[cat]?.[month] ?? 0);
  const realVal = (cat, month) => (progress[cat]?.[month] ?? 0);
  const sumRange = (src, cat, months) => months.reduce((s, m) => s + (src[cat]?.[m] || 0), 0);
  // Valoarea afișată într-o celulă/agregat în funcție de mod
  const disp = (cat, months) => {
    const obj = sumRange(matrix, cat, months);
    const real = sumRange(progress, cat, months);
    if (mode === "realized") return real.toLocaleString("ro-RO");
    if (mode === "percent") return obj > 0 ? Math.round((real / obj) * 100) + "%" : (real > 0 ? "∞" : "—");
    return obj.toLocaleString("ro-RO");
  };
  const pctClass = (cat, months) => {
    if (mode !== "percent") return "";
    const obj = sumRange(matrix, cat, months), real = sumRange(progress, cat, months);
    if (obj === 0) return "";
    const p = real / obj;
    return p >= 1 ? "text-emerald-700" : p >= 0.7 ? "text-amber-600" : "text-red-600";
  };

  // Total canal doar pentru categoriile în unități "contracte" (exclude HVAS/puncte —
  // altfel s-ar aduna puncte cu nr. contracte). Mod-aware: obiectiv / realizat / %.
  const ALL_MONTHS = [1,2,3,4,5,6,7,8,9,10,11,12];
  const channelLabel = (meta.channels || []).find((c) => c.key === channel)?.label || channel;
  const countCats = (meta.categories || []).filter((c) => c.unit !== "puncte");
  const grandObj = countCats.reduce((s, c) => s + sumRange(matrix, c.key, ALL_MONTHS), 0);
  const grandReal = countCats.reduce((s, c) => s + sumRange(progress, c.key, ALL_MONTHS), 0);
  const grandDisplay = mode === "realized"
    ? grandReal.toLocaleString("ro-RO")
    : mode === "percent"
      ? (grandObj > 0 ? Math.round((grandReal / grandObj) * 100) + "%" : (grandReal > 0 ? "∞" : "—"))
      : grandObj.toLocaleString("ro-RO");
  const grandLabel = mode === "realized"
    ? `Total realizat contracte ${channelLabel} ${year} (fără HVAS puncte)`
    : mode === "percent"
      ? `% realizat contracte ${channelLabel} ${year} (fără HVAS)`
      : `Total obiective contracte ${channelLabel} ${year} (fără HVAS puncte)`;

  const handleSave = async () => {
    setSaving(true);
    try {
      const cells = [];
      (meta.categories || []).forEach((c) => {
        for (let m = 1; m <= 12; m++) {
          cells.push({ category: c.key, month: m, objective: matrix[c.key]?.[m] || 0 });
        }
      });
      const r = await axios.post(`${API_URL}/channel-objectives/bulk`, { year, channel, cells });
      toast.success(`Obiective salvate (${r.data.saved} celule)`);
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Nu ai permisiune să salvezi" : "Salvarea a eșuat");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="objectives-matrix-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center">
          <Target className="h-5 w-5 text-indigo-700" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Obiective</h1>
          <p className="text-sm text-slate-500">
            Obiective lunare per canal și produs. Q1-Q4 și Total se calculează automat.
          </p>
        </div>
      </div>

      <Tabs defaultValue="matrix" className="w-full">
        <TabsList data-testid="objectives-tabs">
          <TabsTrigger value="matrix" data-testid="objectives-tab-matrix">Obiective</TabsTrigger>
          <TabsTrigger value="points" data-testid="objectives-tab-points">
            <Award className="h-4 w-4 mr-1.5" />Punctaje produse
          </TabsTrigger>
        </TabsList>

        <TabsContent value="matrix" className="mt-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">An</Label>
                <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                  <SelectTrigger className="w-28" data-testid="objectives-year"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Canal</Label>
                <Select value={channel} onValueChange={setChannel}>
                  <SelectTrigger className="w-44" data-testid="objectives-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(meta.channels || []).map((c) => (
                      <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex rounded-md border overflow-hidden" data-testid="objectives-mode-toggle">
                {[["objective","Obiectiv"],["realized","Realizat"],["percent","% Realizat"]].map(([k,lbl]) => (
                  <button key={k} onClick={() => setMode(k)}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${mode===k ? "bg-indigo-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`objectives-mode-${k}`}>{lbl}</button>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={fetchMatrix} disabled={loading} data-testid="objectives-reload">
                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Reîncarcă
              </Button>
              {canWrite && mode === "objective" && (
                <Button size="sm" onClick={handleSave} disabled={saving || !dirty} data-testid="objectives-save">
                  {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                  Salvează
                </Button>
              )}
            </div>
          </div>
          <CardDescription className="pt-2">
            {channelLabel} · {year}
            {mode !== "objective" && <span className="ml-1 text-slate-400">· realizat măsurat după data CSD</span>}
            {dirty && mode === "objective" && <span className="text-amber-600 font-medium"> · modificări nesalvate</span>}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-slate-500 border-b">
                    <th className="text-left font-semibold px-3 py-2 sticky left-0 bg-white">Categorie</th>
                    {MONTHS.map((m) => <th key={m} className="px-1 py-2 font-medium text-center w-16">{m}</th>)}
                    {QUARTERS.map((q) => <th key={q.key} className="px-2 py-2 font-semibold text-center bg-slate-50">{q.key}</th>)}
                    <th className="px-2 py-2 font-bold text-center bg-slate-100">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(meta.categories || []).map((cat) => (
                    <tr key={cat.key} className="border-b hover:bg-slate-50/50" data-testid={`objectives-row-${cat.key}`}>
                      <td className="px-3 py-2 font-medium text-slate-800 sticky left-0 bg-white">
                        {cat.label}
                        {cat.unit === "puncte" && <span className="text-slate-400 text-xs ml-1">(puncte)</span>}
                        {cat.unit === "puncte" && mode !== "objective" && hvasUnmapped > 0 && (
                          <span className="ml-2 inline-flex items-center rounded bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 font-medium"
                                title="Contracte HVAS fără punctaj mapat — adaugă aliasuri în tab-ul Punctaje produse"
                                data-testid="hvas-unmapped-chip">
                            {hvasUnmapped} nemapate
                          </span>
                        )}
                      </td>
                      {MONTHS.map((_, i) => {
                        const m = i + 1;
                        return (
                          <td key={m} className="px-0.5 py-1">
                            {mode === "objective" ? (
                              <Input
                                type="number" min={0}
                                value={cellVal(cat.key, m)}
                                onChange={(e) => setCell(cat.key, m, e.target.value)}
                                disabled={!canWrite}
                                className="h-8 text-center px-1 text-xs"
                                data-testid={`objectives-cell-${cat.key}-${m}`}
                              />
                            ) : (
                              <div className={`h-8 flex items-center justify-center text-xs ${pctClass(cat.key, [m])}`}
                                   data-testid={`objectives-cell-${cat.key}-${m}`}>
                                {disp(cat.key, [m])}
                              </div>
                            )}
                          </td>
                        );
                      })}
                      {QUARTERS.map((q) => (
                        <td key={q.key} className={`px-2 py-2 text-center font-semibold bg-slate-50 ${pctClass(cat.key, q.months) || "text-slate-700"}`}
                            data-testid={`objectives-${q.key}-${cat.key}`}>
                          {disp(cat.key, q.months)}
                        </td>
                      ))}
                      <td className={`px-2 py-2 text-center font-bold bg-slate-100 ${pctClass(cat.key, [1,2,3,4,5,6,7,8,9,10,11,12]) || "text-slate-900"}`}
                          data-testid={`objectives-total-${cat.key}`}>
                        {disp(cat.key, [1,2,3,4,5,6,7,8,9,10,11,12])}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 bg-indigo-50/40">
                    <td className="px-3 py-2 font-bold text-slate-900 sticky left-0 bg-indigo-50/40">Total canal</td>
                    <td colSpan={MONTHS.length + QUARTERS.length} className="px-2 py-2 text-right text-xs text-slate-500">
                      {grandLabel}:
                    </td>
                    <td className="px-2 py-2 text-center font-bold text-indigo-800 bg-indigo-100" data-testid="objectives-grand-total">
                      {grandDisplay}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="points" className="mt-4">
          <ProductPointsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
